import { anchorPosition, boundsOfValue, type Evaluated } from '@/domain/construction';
import { type Bounds } from '@/domain/geometry';
import { type Vec2 } from '@/domain/math';
import { type Entity, type EntityId, inDrawOrder } from '@/domain/model';
import { queryIndexBounds, type Scene, valueOf } from '@/engine';

import {
  type Camera,
  DEFAULT_CAMERA,
  fitBounds,
  toScreen,
  toWorld,
  screenToWorldDistance,
  type Viewport,
  visibleBounds,
} from './Camera';
import {
  DEFAULT_GRID_STYLE,
  DEFAULT_GRID_VISIBILITY,
  type GridStyle,
  type GridVisibility,
  paintGrid,
} from './grid';
import { ImageCache } from './images';
import { LayerName, LayerStack, type LayerStackOptions } from './LayerStack';
import {
  coveredSegments,
  cutIntoPieces,
  dashedIfCovered,
  withoutLiftedEdges,
} from './painters/hiddenLines';
import { nameDirections } from './painters/namePlacement';
import { type OverlayInput, paintOverlay } from './painters/paintOverlay';
import { paintName, paintValue } from './painters/paintValue';
import { washesFirst } from './paintOrder';
import { type FrameInfo, RenderScheduler, type SchedulerHost } from './RenderScheduler';

/**
 * How far beyond the viewport an entity is still painted, in screen pixels.
 *
 * Sized for the largest thing drawn outside its own geometry: a label beside its anchor, or
 * an angle mark's arc and value. Generous on purpose — the cost of a too-wide margin is a few
 * extra draw calls, and the cost of a too-narrow one is objects blinking out at the edge.
 */
const CULL_MARGIN_PIXELS = 160;

/**
 * Whether a value could put ink inside `view`.
 *
 * The index narrows by *cell*, which is as fine as a grid gets — a cell holding twenty points
 * offers all twenty when one is on screen. This second test is exact, and it is affordable
 * precisely because the index has already made the candidate list short.
 *
 * A value with no bounds is always drawn: a line crossing the viewport is defined by two
 * points that may be nowhere near it, so there is nothing finite to test against.
 */
function touches(value: Evaluated, view: Bounds): boolean {
  const box = boundsOfValue(value);
  if (box === null) return true;
  return (
    box.maxX >= view.minX && box.minX <= view.maxX && box.maxY >= view.minY && box.minY <= view.maxY
  );
}

/**
 * Ties the layer stack, the camera and the frame loop together.
 *
 * The renderer is the only object that paints. It exposes invalidation rather than
 * drawing methods: callers say *what changed*, never *when to draw*. That inversion is
 * what makes the single-frame-loop guarantee of ADR-0004 hold — there is no way for a
 * caller to force a synchronous repaint even by accident.
 *
 * Painting scene entities arrives in M5, once the geometry engine can evaluate them.
 * Until then the content layer has nothing to draw, and the grid, camera, dirty-flag and
 * scheduling machinery below are complete and exercised.
 */

export interface RendererOptions extends LayerStackOptions {
  readonly gridStyle?: GridStyle | undefined;
  readonly schedulerHost?: SchedulerHost | undefined;
  readonly camera?: Camera | undefined;
}

/** What changed since the last frame. Each flag maps to exactly one layer. */
interface DirtyFlags {
  grid: boolean;
  content: boolean;
  overlay: boolean;
}

export class Renderer {
  private readonly layers: LayerStack;
  private readonly scheduler: RenderScheduler;
  private readonly gridStyle: GridStyle;

  private gridVisibility: GridVisibility = DEFAULT_GRID_VISIBILITY;

  private cameraState: Camera;
  private sceneState: Scene | null = null;
  private overlayState: OverlayInput = { selected: [], preview: [], snap: null };
  private readonly dirty: DirtyFlags = { grid: true, content: true, overlay: true };
  private frameListeners: ((info: FrameInfo) => void)[] = [];

  constructor(container: HTMLElement, options: RendererOptions = {}) {
    this.layers = new LayerStack(container, { devicePixelRatio: options.devicePixelRatio });
    this.gridStyle = options.gridStyle ?? DEFAULT_GRID_STYLE;
    this.cameraState = options.camera ?? DEFAULT_CAMERA;
    this.scheduler = new RenderScheduler(this.renderFrame, options.schedulerHost);

    // Every layer starts dirty, so back that intent with an actual scheduled frame.
    // Without this the renderer relies on the first `resize` to report a change — and a
    // container that is already at its final size when the renderer is constructed
    // would never produce one, leaving a blank canvas until some unrelated event.
    this.scheduler.invalidate();
  }

  get camera(): Camera {
    return this.cameraState;
  }

  /**
   * World to screen, in CSS pixels.
   *
   * Exposed because the shell has to place DOM over the drawing — the inline label editor —
   * and only the renderer holds both the camera and the viewport.
   */
  toScreen(world: Vec2): Vec2 {
    return toScreen(this.cameraState, this.layers.viewport, world);
  }

  get viewport(): Viewport {
    return this.layers.viewport;
  }

  get visibleWorldBounds(): Bounds {
    return visibleBounds(this.cameraState, this.layers.viewport);
  }

  get framesRendered(): number {
    return this.scheduler.frames;
  }

  /**
   * Moves the camera.
   *
   * Dirties every layer: a camera change moves all three. This is the one invalidation
   * that cannot be narrowed to a region, which is exactly why panning is the most
   * expensive interaction in a canvas editor and why the grid layer exists to keep it
   * from also rebuilding entity paths.
   */
  setCamera(next: Camera): void {
    if (
      next.x === this.cameraState.x &&
      next.y === this.cameraState.y &&
      next.zoom === this.cameraState.zoom
    ) {
      return;
    }
    this.cameraState = next;
    this.dirty.grid = true;
    this.dirty.content = true;
    this.dirty.overlay = true;
    this.scheduler.invalidate();
  }

  fit(target: Bounds, padding?: number): void {
    this.setCamera(fitBounds(this.cameraState, this.layers.viewport, target, padding));
  }

  /** Converts a pointer position (CSS pixels, relative to the container) to world space. */
  screenToWorld(screen: Vec2): Vec2 {
    return toWorld(this.cameraState, this.layers.viewport, screen);
  }

  /**
   * Reports a new viewport size.
   *
   * Only schedules a frame when the size actually changed — `ResizeObserver` fires on
   * every layout pass, and resizing a canvas clears it, so an unconditional resize would
   * both waste frames and blank the screen during unrelated layout work.
   */
  resize(width: number, height: number): void {
    if (!this.layers.resize(width, height)) return;
    this.dirty.grid = true;
    this.dirty.content = true;
    this.dirty.overlay = true;
    this.scheduler.invalidate();
  }

  get scene(): Scene | null {
    return this.sceneState;
  }

  /**
   * Supplies the evaluated scene to paint.
   *
   * Takes the whole scene rather than a diff: the scene is an immutable snapshot, so
   * swapping the reference is cheap, and the caller already knows which entities changed
   * and can invalidate accordingly. Passing a diff here would duplicate that knowledge
   * in two places.
   */
  setScene(scene: Scene | null): void {
    if (scene === this.sceneState) return;
    this.sceneState = scene;
    this.dirty.content = true;
    this.scheduler.invalidate();
  }

  /**
   * Supplies what the overlay should show: selection, tool preview, snap indicator.
   *
   * Separate from `setScene` because the overlay changes on every pointer move while the
   * scene changes only on an edit — coupling them would repaint entity paths for a
   * hover.
   */
  setOverlay(overlay: OverlayInput): void {
    this.overlayState = overlay;
    this.dirty.overlay = true;
    this.scheduler.invalidate();
  }

  /**
   * Shows or hides the two axes through the world origin.
   *
   * Only the grid layer is dirtied, which is the whole reason the layer exists: toggling the
   * backdrop must not rebuild a single entity path. The grid lines themselves stay — a figure
   * is usually easier to read without the two heavy lines crossing it, and that is a different
   * request from wanting no ruling at all.
   *
   * A no-op when nothing changes, so a component pushing its state on every render does not
   * burn a frame per render.
   */
  setGridAxesVisible(visible: boolean): void {
    if (this.gridVisibility.axes === visible) return;
    this.gridVisibility = { axes: visible };
    this.dirty.grid = true;
    this.scheduler.invalidate();
  }

  invalidateContent(): void {
    this.dirty.content = true;
    this.scheduler.invalidate();
  }

  invalidateOverlay(): void {
    this.dirty.overlay = true;
    this.scheduler.invalidate();
  }

  /** Subscribes to frames. Returns an unsubscribe function. */
  onFrame(listener: (info: FrameInfo) => void): () => void {
    this.frameListeners.push(listener);
    return () => {
      this.frameListeners = this.frameListeners.filter((l) => l !== listener);
    };
  }

  dispose(): void {
    this.images.clear();
    this.scheduler.dispose();
    this.frameListeners = [];
    this.layers.dispose();
  }

  private readonly renderFrame = (info: FrameInfo): void => {
    const { width, height } = this.layers.viewport;
    if (width > 0 && height > 0) {
      if (this.dirty.grid) this.paintGridLayer();
      if (this.dirty.content) this.paintContentLayer();
      if (this.dirty.overlay) this.paintOverlayLayer();
    }

    this.dirty.grid = false;
    this.dirty.content = false;
    this.dirty.overlay = false;

    for (const listener of this.frameListeners) listener(info);
  };

  private paintGridLayer(): void {
    this.layers.clear(LayerName.Grid);
    this.layers.applyIdentity(LayerName.Grid);
    paintGrid(
      this.layers.get(LayerName.Grid).context,
      this.cameraState,
      this.layers.viewport,
      this.gridStyle,
      this.gridVisibility
    );
  }

  private paintContentLayer(): void {
    this.layers.clear(LayerName.Content);
    // Identity transform: painters work in screen space so that stroke widths, point
    // sizes and dash patterns stay constant in pixels at every zoom level. See
    // `painters/paintValue.ts`.
    this.layers.applyIdentity(LayerName.Content);

    const scene = this.sceneState;
    if (scene === null) return;

    const paint = this.paintContextFor(LayerName.Content);
    const onScreen = this.visibleEntities(scene);
    const view = this.paddedViewBounds();

    // Where each name goes depends on the edges around it, so it is a fact about the whole
    // scene and cannot be worked out inside the per-entity loop below.
    const nameAway = nameDirections(scene);
    // Whether a solid covers a line is a fact about the view, so it is answered per frame
    // rather than stored — the same pass the exports run, so the screen and the page agree.
    const covered = coveredSegments(scene);

    for (const entity of washesFirst(inDrawOrder(scene.document), (entity) => entity.style)) {
      if (!onScreen.has(entity.id)) continue;
      if (!entity.visible) continue;
      if (!this.showEntity(entity)) continue;
      const value = valueOf(scene, entity.id);
      // A construction with no value simply is not drawn — and reappears by itself when
      // it becomes defined again.
      if (value === null) continue;
      if (!touches(value, view)) continue;
      /*
       * One paint call per stretch, so a line running out from behind a block is drawn dashed
       * where the block covers it and solid where it does not. A segment nothing cuts comes back
       * as a single piece, which is every ordinary line on the page.
       */
      for (const piece of cutIntoPieces(
        // A solid lets go of any edge that is now a segment of its own, or the two are drawn
        // one over the other and the lifted one's dashes show the original through their gaps.
        withoutLiftedEdges(value, covered.lifted.get(entity.id) ?? []),
        covered.parts.get(entity.id)
      )) {
        paintValue(
          paint,
          piece.value,
          dashedIfCovered(entity.style, piece.covered || covered.whole.has(entity.id)),
          covered.edges.get(entity.id),
          covered.liftedEdges.get(entity.id)
        );
      }

      // The name is drawn after the object, so a thick stroke never covers it.
      if (entity.label !== undefined) {
        const at = anchorPosition(value);
        if (at !== null) paintName(paint, at, entity.label, entity.style, nameAway.get(entity.id));
      }
    }
  }

  /**
   * The entities whose geometry could touch the visible area.
   *
   * ## Why cull at all when the canvas already clips
   *
   * A `CanvasRenderingContext2D` discards what falls outside its surface, so an off-screen
   * object costs nothing to *rasterise* — but it costs everything before that. Every value
   * still gets its own `beginPath`, its world-to-screen conversions, its style resolution and
   * its `stroke` call, and the driver still validates each one. Zooming into a corner of a
   * large worksheet repainted the whole document on every frame to draw four segments of it.
   *
   * ## Margin, not an exact rectangle
   *
   * The visible rectangle is widened before it is asked about, because the index answers by
   * *geometry* bounds and several things are drawn outside them: a point is a disc of screen
   * pixels around a mathematical point, a label sits beside its anchor, an angle mark is drawn
   * at a screen-space radius. Culling on the exact rectangle would make those pop away at the
   * edge — a visual defect far more noticeable than the work it saves.
   */
  private visibleEntities(scene: Scene): ReadonlySet<EntityId> {
    return queryIndexBounds(scene.index, this.paddedViewBounds());
  }

  /**
   * The visible world rectangle, widened by the margin.
   *
   * Used twice per frame and deliberately not cached: it is four arithmetic operations, and a
   * cache would be one more thing to invalidate when the camera moves.
   */
  private paddedViewBounds(): Bounds {
    const view = visibleBounds(this.cameraState, this.layers.viewport);
    const margin = screenToWorldDistance(this.cameraState, CULL_MARGIN_PIXELS);
    return {
      minX: view.minX - margin,
      minY: view.minY - margin,
      maxX: view.maxX + margin,
      maxY: view.maxY + margin,
    };
  }

  /**
   * Restricts what the content layer draws, without touching the document.
   *
   * Three features need this and none of them is a property of the drawing: replaying a
   * construction step by step, hiding helper objects during a lesson, and presenting a
   * finished figure. Each is a statement about *this view right now*, so none belongs in
   * the file — a worksheet emailed to a class must not arrive half-revealed because the
   * teacher left the replay paused.
   *
   * Passing `null` restores everything.
   */
  setVisibilityFilter(filter: ((entity: Entity) => boolean) | null): void {
    this.visibilityFilter = filter;
    this.dirty.content = true;
    this.scheduler.invalidate();
  }

  private showEntity(entity: Entity): boolean {
    return this.visibilityFilter === null || this.visibilityFilter(entity);
  }

  private visibilityFilter: ((entity: Entity) => boolean) | null = null;

  private paintOverlayLayer(): void {
    this.layers.clear(LayerName.Overlay);
    this.layers.applyIdentity(LayerName.Overlay);

    /*
     * The early-out has to name every channel. A frame with no selection halo — a tool showing
     * where a transform would act before anything is highlighted — is a real state, and leaving
     * `box` out of this test would drop it silently rather than fail visibly.
     */
    const { selected, preview, snap, box } = this.overlayState;
    if (selected.length === 0 && preview.length === 0 && snap === null && box == null) return;

    paintOverlay(this.paintContextFor(LayerName.Overlay), this.overlayState);
  }

  /** Builds the screen-space paint context shared by the content and overlay layers. */
  private paintContextFor(layer: LayerName) {
    const viewport = this.layers.viewport;
    const cameraState = this.cameraState;
    return {
      ctx: this.layers.get(layer).context,
      toScreen: (world: Vec2) => toScreen(cameraState, viewport, world),
      worldBounds: visibleBounds(cameraState, viewport),
      zoom: cameraState.zoom,
      images: this.images,
    };
  }

  /**
   * Decoded images, shared by every layer.
   *
   * A decode that finishes between frames invalidates the content layer, which is what makes
   * an imported picture appear a frame after it is placed rather than never.
   */
  private readonly images = new ImageCache(() => {
    this.dirty.content = true;
    this.scheduler.invalidate();
  });
}
